import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePersonalizedPath } from '../../hooks/usePersonalizedPath';
import { microSessionService } from '@/services/microSessionService';
import { getChallengeOption } from '@/data/challengeOptions';
import { MicroSessionCard } from '../micro/MicroSessionCard';
import { useMicroPlaylist } from '@/hooks/useMicroPlaylist';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface PersonalizedCatalogProps {
  expanded?: boolean;
  onToggle?: () => void;
}

const PersonalizedCatalog: React.FC<PersonalizedCatalogProps> = ({ expanded = true, onToggle }) => {
  const { path, loading, error } = usePersonalizedPath();
  const { progressMap, loading: progressLoading } = useMicroPlaylist();
  const navigate = useNavigate();

  // Don't render anything if loading or no path - let parent handle
  if (loading || progressLoading || error || !path) {
    return null;
  }

  const challengeOption = getChallengeOption(path.challenge);
  if (!challengeOption) return null;

  const quickWinLesson = challengeOption.quickWinId
    ? microSessionService.getSessionById(challengeOption.quickWinId)
    : null;

  const rephraseTitle = (title: string) => {
    // Warm companion approach - validate effort, promise relief
    return "You made space to be here—let's lighten the load.";
  };

  const buildSituationLine = (problem: string) => {
    // Acknowledge reality without dwelling - keep it simple and warm
    if (!problem) return "We'll work on what's weighing you down together.";

    // Use the problem to create simple acknowledgment
    const isTimeRelated = problem.toLowerCase().includes('hour') || problem.toLowerCase().includes('time');
    const isOverwhelm = problem.toLowerCase().includes('too many') || problem.toLowerCase().includes('everything');

    if (isTimeRelated) {
      return "Writing takes up most of your week right now. We'll work on that together.";
    } else if (isOverwhelm) {
      return "You're juggling a lot right now. We'll start making this easier together.";
    }

    // Default: simple acknowledgment
    return "There's a lot on your plate. We'll tackle it together.";
  };

  const buildPlanLine = () => {
    // Find the next open lesson (first non-completed lesson in path)
    const nextOpenLessonId = path.path.find(lessonId => {
      const progress = progressMap[lessonId];
      return progress !== 'completed';
    });

    const nextOpenLesson = nextOpenLessonId
      ? microSessionService.getSessionById(nextOpenLessonId)
      : null;

    const nextLessonTitle = nextOpenLesson?.title || 'your next lesson';

    return `First move: ${nextLessonTitle.toLowerCase()}. You'll feel the difference today.`;
  };

  const headline = rephraseTitle(challengeOption.title);
  const situationLine = buildSituationLine(challengeOption.problem);
  const planLine = buildPlanLine();

  const handleStart = (session: any) => {
    microSessionService.startSession(session.id);
    navigate(session.route);
  };

  return (
    <section className="rounded-[32px] border border-slate-200 bg-white p-6 space-y-6 shadow-sm">
      {/* Minimal Header - Integrated into Quick Access Hub Style */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Your personalized path</p>
          <h2 className="text-lg font-semibold text-slate-900">{headline}</h2>
          <p className="text-sm text-slate-600 mt-1">{situationLine}</p>
        </div>
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="rounded-full border border-slate-200 p-2 text-slate-500 hover:border-slate-400 hover:text-slate-700 transition"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <span className="sr-only">{expanded ? 'Collapse personalized path' : 'Expand personalized path'}</span>
          </button>
        )}
      </div>

      {expanded && (
        <>
          {/* Progress Indicator */}
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-slate-500">
            <span>Lesson {path.current_index + 1} of {path.path.length}</span>
            <span>{challengeOption.estimatedTotalTime} min total</span>
          </div>

          {/* Lesson Grid - Using Shared MicroSessionCard */}
          <div className="grid gap-4 md:grid-cols-2">
            {path.path.map((lessonId, index) => {
              const session = microSessionService.getSessionById(lessonId);

              // If lesson not found, show minimal placeholder
              if (!session) {
                return (
                  <div key={`${lessonId}-${index}`} className="rounded-[24px] border border-dashed border-slate-200 p-5 opacity-50">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs uppercase tracking-[0.2em] text-slate-400">Lesson {index + 1}</span>
                      <span className="text-xs text-slate-400">Coming soon</span>
                    </div>
                    <p className="text-sm text-slate-400 font-mono">{lessonId}</p>
                  </div>
                );
              }

              const isCurrent = index === path.current_index;
              const isQuickWin = lessonId === path.quickWin;

              // Determine status from actual progress data, not just index comparison
              // This prevents marking skipped lessons as completed
              const actualProgress = progressMap[lessonId];
              const status = actualProgress === 'completed'
                ? 'completed'
                : (isCurrent || actualProgress === 'in_progress')
                  ? 'in_progress'
                  : 'not_started';

              return (
                <MicroSessionCard
                  key={`${lessonId}-${index}`}
                  session={session}
                  onStart={handleStart}
                  status={status}
                  overline={`Lesson ${index + 1}`}
                  recommended={isQuickWin && actualProgress !== 'completed'}
                />
              );
            })}
          </div>
        </>
      )}
    </section>
  );
};

export default PersonalizedCatalog;
